import {Box, Flex, IconButton, Text, Tooltip} from '@chakra-ui/react';
import {motion, useReducedMotion} from 'framer-motion';
import {TablerX} from '../../icons';
import MetaPill from '../common/MetaPill';
import usePlannerStyles from '../planner/usePlannerStyles';
import type {UniversityVideo} from '../../services/universityService';
import UniversityPoster from './UniversityPoster';
import {formatVideoDuration} from './UniversityVideoModal';

const MotionBox = motion(Box);

// ─── Contextual "Watch: how to…" launcher ────────────────────────────────────
//
// A small docked card offering the page's how-to video at the moment of need.
// Deliberately polite: never autoplays, never blocks, and one dismissal is
// permanent (persisted per-user on the backend, so it stays dismissed across
// devices). Anchored bottom-LEFT on sm+: the bottom-right corner is already a
// stack on the note pages (DictationFab, its voice-edit hint popup, the EHR
// push status dock) and stacking a fourth card there clobbered the hint. On
// md+ the left offset clears the collapsed sidebar rail. On phones the card
// is effectively full-width, parked above the mobile Copy/Push footer band
// (`calc(5.5rem + safe-area)` FAB row).
const UniversityContextTip = ({
  video,
  onOpen,
  onDismiss,
}: {
  video: UniversityVideo;
  onOpen: (video: UniversityVideo) => void;
  onDismiss: (video: UniversityVideo) => void;
}) => {
  const styles = usePlannerStyles();
  const prefersReducedMotion = useReducedMotion();

  return (
    <MotionBox
      data-testid="university-context-tip"
      position="fixed"
      bottom={{
        base: 'calc(9.5rem + env(safe-area-inset-bottom, 0px))',
        sm: 6,
      }}
      left={{base: 4, sm: 6, md: 20}}
      // Page content sits at 1, while the expanding sidebar shadow and rail
      // sit at 8 and 9. Keep the tip over the page without letting it cover
      // sidebar navigation when the rail expands across the card.
      zIndex={7}
      w={{base: 'calc(100vw - 2rem)', sm: '340px'}}
      maxW="340px"
      initial={
        prefersReducedMotion ? {opacity: 0} : {opacity: 0, y: 12, scale: 0.98}
      }
      animate={
        prefersReducedMotion ? {opacity: 1} : {opacity: 1, y: 0, scale: 1}
      }
      exit={
        prefersReducedMotion ? {opacity: 0} : {opacity: 0, y: 8, scale: 0.98}
      }
      transition={{duration: 0.2, ease: [0.2, 0, 0, 1]}}
    >
      <Flex
        align="stretch"
        gap={0}
        bg={styles.surfaceBg}
        borderWidth="1px"
        borderColor={styles.borderColor}
        borderRadius="xl"
        boxShadow={styles.cardShadow}
        overflow="hidden"
      >
        <Box
          as="button"
          type="button"
          aria-label={`Watch: ${video.title}`}
          flexShrink={0}
          w="104px"
          position="relative"
          onClick={() => onOpen(video)}
          _focusVisible={styles.focusOutlineStyles}
        >
          <UniversityPoster
            video={video}
            iconSize={14}
            position="absolute"
            inset={0}
          />
        </Box>
        <Box
          as="button"
          type="button"
          flex="1 1 auto"
          minW={0}
          textAlign="left"
          px={3}
          py={2.5}
          onClick={() => onOpen(video)}
          _hover={{bg: styles.hoverBg}}
          _focusVisible={styles.focusOutlineStyles}
        >
          <Text
            fontSize="2xs"
            fontWeight="medium"
            color={styles.metaTextColor}
            textTransform="uppercase"
            letterSpacing="0.05em"
          >
            Quick video
          </Text>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color={styles.textColor}
            lineHeight="1.3"
            mt={0.5}
            noOfLines={2}
          >
            {video.title}
          </Text>
          <MetaPill mt={1.5} borderRadius="md">
            {formatVideoDuration(video.durationSec)}
          </MetaPill>
        </Box>
        <Box pt={1.5} pr={1.5}>
          <Tooltip label="Dismiss" hasArrow openDelay={300}>
            <IconButton
              type="button"
              aria-label="Dismiss video tip"
              icon={<TablerX size={14} aria-hidden="true" />}
              size="xs"
              variant="ghost"
              borderRadius="md"
              color={styles.metaTextColor}
              onClick={() => onDismiss(video)}
              _hover={{bg: styles.rowHoverBg}}
              _focusVisible={styles.focusOutlineStyles}
            />
          </Tooltip>
        </Box>
      </Flex>
    </MotionBox>
  );
};

export default UniversityContextTip;
